import type { ArticleData } from "@/types/article";

interface WebSiteSchemaProps {
  url: string;
  name: string;
  description: string;
}

interface ArticleSchemaProps {
  article: ArticleData;
  url: string;
  publisherName: string;
  publisherUrl: string;
  imageUrl?: string;
}

export function WebSiteJsonLd({ url, name, description }: WebSiteSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    url,
    name,
    description,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${url}/?s={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function ArticleJsonLd({ article, url, publisherName, publisherUrl, imageUrl }: ArticleSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.meta.title,
    description: article.meta.excerpt,
    datePublished: article.meta.date,
    url,
    image: imageUrl || undefined,
    author: {
      "@type": "Organization",
      name: publisherName,
      url: publisherUrl,
    },
    publisher: {
      "@type": "Organization",
      name: publisherName,
      url: publisherUrl,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function FAQJsonLd({ questions, url }: { questions: { question: string; answer: string }[]; url: string }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map((q) => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.answer,
      },
    })),
    url,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}